import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'

/**
 * Everything modal renders through here.
 *
 * **Why a portal is mandatory rather than tidy.** The shell's stage is a frosted panel —
 * `backdrop-filter: blur(30px)` — and a `backdrop-filter` makes an element the containing
 * block for every `position: fixed` descendant, exactly as `transform` and `filter` do. A
 * dialog written the obvious way (`fixed inset-0`) therefore resolves `inset: 0` against the
 * *panel* instead of the viewport: measured at 1200×897, the scrim covered (271, 13) 916×871
 * and left the rail and the desk margin fully lit and fully clickable.
 *
 * Nothing about that fails loudly. The dialog opens, traps focus, and works; it simply stops
 * being modal, and only in the themes and viewports where someone happens to look at the
 * edge. So the fix lives in one component that every dialog goes through, rather than in a
 * comment asking six of them to remember.
 *
 * `document.body` is deliberate: any other host would have to promise, forever, that no
 * ancestor of it ever grows a filter, a transform or a containment.
 */
export const Overlay = ({ children }: { children: ReactNode }) => createPortal(children, document.body)
